import { useEffect, type ReactNode } from 'react';
import { BRAND } from '@platform/shared';
import { Link } from '../lib/router';
import { toggleTheme, useTheme } from '../lib/theme';
import { LoginShowcase } from './LoginShowcase';
import { Icon } from './ui';

/**
 * Full-screen split layout for the auth pages (rendered outside the app chrome): form column on the
 * left, marketing panel on the right (desktop only). Follows the light/dark theme; the panel stays dark.
 */
/** `wide`: a wider form column, for pages with more on them (sign-up steps). */
export function AuthLayout({ title, children, legalFooter = true, wide = false }: { title: string; children: ReactNode; legalFooter?: boolean; wide?: boolean }) {
  const theme = useTheme();
  useEffect(() => {
    document.title = `${title} · ${BRAND.name}`;
  }, [title]);

  return (
    <div className="grid min-h-full bg-bg text-[14px] text-fg lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="flex flex-col px-4 py-6 sm:px-[clamp(24px,6vw,96px)] sm:py-8">
        <header className="flex items-center justify-between gap-3">
          <Link to="/trade" className="flex items-center text-lg font-bold tracking-tight text-fg no-underline">
            {BRAND.name}
          </Link>
          <div className="flex items-center gap-1 rounded-full bg-raised p-1.5">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              className="grid size-8 place-items-center rounded-full text-muted hover:bg-bg hover:text-fg"
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
            </button>
          </div>
        </header>

        <main className={`mx-auto flex w-full flex-1 flex-col justify-center py-12 lg:mx-0 ${wide ? 'max-w-[560px]' : 'max-w-[440px]'}`}>
          {children}

          <p className="mt-[18px] flex items-center justify-center gap-2 text-[13px] text-muted">
            <Icon name="shield" size={14} />
            Encrypted connection · 2-step verification
          </p>
        </main>

        {legalFooter && (
          <p className="m-0 max-w-[440px] text-[13px] leading-relaxed text-muted [&_a]:text-fg [&_a]:underline">
            By continuing, you agree to the <Link to="/terms">Terms of Use</Link>,{' '}
            <Link to="/privacy">Privacy Notice</Link> and <Link to="/risk-disclosure">Risk Disclosure</Link>.
          </p>
        )}
      </div>

      <LoginShowcase />
    </div>
  );
}
